import { useFormContext, Controller, type FieldValues, type Path } from 'react-hook-form';
import { DatePicker } from 'pink-lava-ui';
import dayjs, { Dayjs } from 'dayjs';

export const DateInput = <T extends FieldValues = any>({
  name,
  label,
  placeholder,
  disabled,
  required,
  showErrorMessage,
  dateFormat = {
    format: 'DD MMM YYYY',
    type: 'mask',
  },
  variant,
  onChange,
  onChangeCallback,
  info,
}: {
  name: string | Path<T>;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  showErrorMessage?: boolean;
  dateFormat?: {
    format: string;
    type?: 'mask';
  };
  variant?: 'filled' | 'borderless' | 'underlined' | 'outlined';
  onChange?: (_date: Dayjs | null, _fieldOnChange: (_date: Dayjs | null) => void) => void;
  onChangeCallback?: (_date: Dayjs | null, _setValue: (_name: keyof T & string, _value: any) => void) => void;
  info?: string;
}) => {
  const { control, setValue } = useFormContext();

  return (
    <>
      <Controller
        name={name}
        control={control}
        rules={{ required }}
        render={({ field, fieldState: { error } }) => {
          return (
            <div className="gap-2 w-full">
              {label && (
                <label className="block text-sm font-medium text-gray-700 mb-1 font-nunito">
                  {label}
                  {required && <span className="text-red-500 ml-1">*</span>}
                </label>
              )}
              <DatePicker
                variant={variant}
                format={dateFormat}
                placeholder={placeholder}
                value={field?.value ? (typeof field.value === 'string' ? dayjs(field.value) : field.value) : null}
                className={showErrorMessage && error?.message ? 'border-red-500!' : ''}
                onChange={date => {
                  console.log('DatePicker onChange called with:', date);
                  const finalValue = date === undefined || date === null ? null : date;
                  if (onChange) {
                    // Scenario 1: onChange overrides default behavior
                    onChange(finalValue, field.onChange);
                  } else {
                    // Default behavior: update form field
                    field.onChange(finalValue);
                    // Scenario 2: onChangeCallback executes alongside default behavior
                    onChangeCallback?.(finalValue, setValue);
                  }
                }}
                onBlur={field.onBlur}
                name={field.name}
                disabled={disabled}
                allowClear={true}
                classNames={{
                  root: 'w-full!',
                }}
              />
              {info && <p className="text-gray-500 text-xs mt-1">{info}</p>}
              {showErrorMessage && error?.message && <p className="text-red-500 text-xs mt-1">{error.message}</p>}
            </div>
          );
        }}
      />
    </>
  );
};
